import React, { useCallback } from 'react';
import Slider, { createSliderWithTooltip } from 'rc-slider';
import classnames from 'classnames';
import { useSearchParams } from 'react-router-dom';

import { roundNumber } from 'buyside/util/formatNumber';
import { debounce } from 'buyside/util/lodashAlt';

import styles from './filterSliderSingleStyles.scss';

export const SliderWithTooltip = createSliderWithTooltip(Slider);

const dotStyle = {
  borderColor: 'transparent',
  backgroundColor: 'transparent',
  outline: 0
};

const handleStyle = {
  backgroundColor: '#4a90e2',
  border: '4px solid #1f6ac2',
  outline: 0,
  marginTop: '-5px',
  marginLeft: 0,
  width: '14px',
  height: '14px',
  cursor: 'pointer',
  borderRadius: '50%'
};

const trackStyle = {
  backgroundColor: '#4a90e2'
};

const FilterSliderSingle: React.FC<{
  interval: number;
  paramName: string;
  ticks: number[];
  className?: string;
  debounceDuration?: number;
  round?: number;
  text?: string;
}> = ({ ticks, interval, text, round = 0, paramName, debounceDuration = 1500, className }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const currentParamValue = searchParams.get(paramName);

  const setParamValue = useCallback(
    (paramValue: string) => {
      searchParams.set('page', '1');
      if (paramValue) {
        searchParams.set(paramName, paramValue);
      } else {
        searchParams.delete(paramName);
      }
      searchParams.delete('recommended');
      searchParams.delete('fpid');
      setSearchParams(searchParams);
    },
    [paramName, searchParams, setSearchParams]
  );

  const parsedCurrentValue = currentParamValue ? parseInt(currentParamValue) : 0;
  const [sliderParsedValue, setParsedValue] = React.useState(parsedCurrentValue);
  const delayedQuery = debounce(
    (value: number): void => setParamValue(value !== 0 ? value.toString() : ''),
    debounceDuration
  );

  const calculateStep = 100 / (Math.abs(ticks[0] - ticks[ticks.length - 1]) / interval);
  const calculateMarks = (): { [number: number]: string } => {
    return ticks.reduce((marks: { [number: number]: string }, tick: number | string, index) => {
      marks[index * 20] = typeof tick === 'number' ? tick.toString() : tick;

      return marks;
    }, {});
  };

  React.useEffect(() => {
    if (!parsedCurrentValue) setParsedValue(0);
  }, [parsedCurrentValue]);

  return (
    <div className={classnames(styles.container, className)}>
      {text && <label>{text}</label>}

      <SliderWithTooltip
        className={styles.slider}
        defaultValue={parsedCurrentValue}
        tipFormatter={(sliderValue): string => {
          return roundNumber(
            ticks[0] + (sliderValue * Math.abs(ticks[0] - ticks[ticks.length - 1])) / 100,
            round
          );
        }}
        value={sliderParsedValue}
        onChange={(value): void => {
          setParsedValue(value);
        }}
        marks={calculateMarks()}
        onAfterChange={delayedQuery}
        step={calculateStep}
        dotStyle={dotStyle}
        handleStyle={handleStyle}
        trackStyle={trackStyle}
      />
    </div>
  );
};

export default FilterSliderSingle;
